import { Badge, Button, EmptyStateHelp, LearnMoreLink } from '@auctisync/ui';
import type { Tables } from '@auctisync/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type Consignor = Tables<'consignors'>;

const STATUS_FILTERS = ['active', 'pending', 'inactive', 'archived'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number] | 'all';

/**
 * Consignor list. Lightweight CRUD — full commission statements and
 * payouts land in Module #13.
 */
export function AdminConsignors() {
  const [rows, setRows] = useState<Consignor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      let query = supabase
        .from('consignors')
        .select('*')
        .order('display_name', { ascending: true });
      if (status !== 'all') query = query.eq('status', status);
      const trimmed = search.trim();
      if (trimmed) {
        const escaped = trimmed.replace(/[%_,]/g, (c) => `\\${c}`);
        const pattern = `%${escaped}%`;
        query = query.or(
          [
            `display_name.ilike.${pattern}`,
            `company.ilike.${pattern}`,
            `email.ilike.${pattern}`,
            `phone.ilike.${pattern}`,
          ].join(','),
        );
      }
      const { data, error: err } = await query;
      if (cancelled) return;
      if (err) setError(err.message);
      else setRows(data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [search, status]);

  const hasResults = rows.length > 0;
  const hasFilters = Boolean(search || status !== 'active');

  return (
    <div>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          marginBottom: '1.25rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Consignors
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.35rem' }}>
            Contacts whose items you sell. Statements + payouts arrive in Module #13.
          </p>
          <LearnMoreLink helpSlug="inventory-overview" newTab>
            How consignors + inventory connect
          </LearnMoreLink>
        </div>
        <Button asChild>
          <Link to="/admin/consignors/new">+ New consignor</Link>
        </Button>
      </header>

      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: '1rem',
          padding: '0.75rem',
          background: 'var(--surface-elev)',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
        }}
      >
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name, company, email, phone…"
          style={{
            flex: '1 1 18rem',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: '0.875rem',
          }}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          style={{
            padding: '0.5rem 0.6rem',
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: '0.875rem',
          }}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          Failed to load consignors: {error}
        </div>
      )}

      {!loading && !hasResults && !hasFilters && (
        <EmptyStateHelp
          title="No consignors yet"
          description="Create your first consignor to start attributing inventory items. Required before you can track commissions."
          helpSlug="inventory-overview"
          primaryActionLabel="Create a consignor"
          primaryActionHref="/admin/consignors/new"
        />
      )}

      {(loading || hasResults) && (
        <div
          style={{
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-card, 0.5rem)',
            overflow: 'hidden',
            background: 'var(--surface-elev)',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr
                  style={{
                    background: 'var(--surface-muted)',
                    borderBottom: '1px solid var(--admin-hairline, var(--border))',
                  }}
                >
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Company</th>
                  <th style={thStyle}>Email</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Commission</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Items</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? [0, 1, 2, 3].map((i) => (
                      <tr
                        key={i}
                        style={{ borderBottom: '1px solid var(--admin-hairline, var(--border))' }}
                      >
                        {[0, 1, 2, 3, 4, 5].map((j) => (
                          <td key={j} style={tdStyle}>
                            <span
                              style={{
                                display: 'inline-block',
                                height: '0.875rem',
                                width: '60%',
                                background: 'var(--surface-muted)',
                                borderRadius: '0.25rem',
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  : rows.map((row) => (
                      <tr
                        key={row.id}
                        style={{ borderBottom: '1px solid var(--admin-hairline, var(--border))' }}
                      >
                        <td style={tdStyle}>
                          <Link
                            to={`/admin/consignors/${row.id}`}
                            style={{ color: 'var(--brand-accent)' }}
                          >
                            {row.display_name}
                          </Link>
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {row.company ?? '—'}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                          {row.email ?? '—'}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {(row.commission_rate_bps / 100).toFixed(2)}%
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {row.total_items_consigned}
                        </td>
                        <td style={tdStyle}>
                          <StatusBadge status={row.status} />
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') return <Badge variant="success">Active</Badge>;
  if (status === 'pending') return <Badge variant="outline">Pending</Badge>;
  if (status === 'inactive') return <Badge variant="outline">Inactive</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  verticalAlign: 'middle',
};
